<template>
  <picture class="hero-banner-image">
    <source
      v-if="desktopSrcset"
      media="(min-width: 768px)"
      :srcset="desktopSrcset"
      sizes="100vw"
    >
    <img
      :src="mobileSrc"
      :srcset="mobileSrcset || undefined"
      sizes="100vw"
      :alt="alt"
      class="hero-banner-image__img"
      :class="fit === 'contain' ? 'object-contain' : 'object-cover'"
      :loading="loading"
      :fetchpriority="fetchpriority"
      decoding="async"
      @load="onLoad"
    >
  </picture>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  desktopImage?: string | null
  mobileImage?: string | null
  alt: string
  fit?: 'cover' | 'contain'
  loading?: 'lazy' | 'eager'
  fetchpriority?: 'high' | 'low' | 'auto'
}>(), {
  fit: 'cover',
  loading: 'lazy',
  fetchpriority: 'auto',
})

const emit = defineEmits<{
  load: [dimensions: { width: number; height: number }]
}>()

const img = useImage()
const { mediaUrl } = useMediaUrl()

function buildSrc(url: string | null | undefined, width: number) {
  if (!url) return ''
  return img(mediaUrl(url), {
    width,
    quality: 78,
    format: 'webp',
    fit: 'cover',
  })
}

function buildSrcset(url: string | null | undefined, widths: number[]) {
  if (!url) return ''
  return widths
    .map((width) => `${buildSrc(url, width)} ${width}w`)
    .join(', ')
}

const desktopSrcset = computed(() =>
  buildSrcset(props.desktopImage, [768, 1024, 1280, 1536]),
)

const mobileSrcset = computed(() =>
  buildSrcset(props.mobileImage || props.desktopImage, [400, 640, 768, 960]),
)

const mobileSrc = computed(() =>
  buildSrc(props.mobileImage || props.desktopImage, 768),
)

function onLoad(event: Event) {
  const imgEl = event.target as HTMLImageElement
  if (imgEl.naturalWidth <= 0 || imgEl.naturalHeight <= 0) return

  emit('load', {
    width: imgEl.naturalWidth,
    height: imgEl.naturalHeight,
  })
}
</script>

<style scoped>
.hero-banner-image {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.hero-banner-image__img {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
